import { DateSelectionMenu } from '@dealstack/frontend/app/pages/Project/Cap/common/DateSelectMenu/dateSelectMenu';

import { EmptyState } from '@dealstack/frontend/shared/components/EmptyState/EmptyState';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { useContext, useMemo } from 'react';
import { WideViewOwnershipTable } from './WideViewOwnershipTable/WideViewOwnershipTable';
import {
	HoldingsType,
	ViewMode,
	useHoldingsController,
} from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/useHoldingsController.tsx';
import { RegularViewTable } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/RegularViewTable/RegularViewTable.tsx';
import { useHoldingsBulkAction } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/useHoldingsBulkAction.ts';
import { DealContext } from '@dealstack/frontend/app/pages/Project/DealDashboard/DealContext';
import { DIRECTORY_BREADCRUMBS } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/shared/directoryBreadcrumbs.tsx';
import { PEOPLE_ROUTES } from '@dealstack/frontend/app/pages/Routing/routes.ts';
import { useBreadcrumbs } from '@dealstack/frontend/shared/components/Breadcrumbs/useBreadcrumbs.tsx';
import { Flex } from '@mantine/core';
import { LabeledSegmentedControl } from '@dealstack/frontend/shared/components/LabeledSegmentedControl/LabeledSegmentedControl.tsx';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import { PermissionWrapper } from '@dealstack/frontend/shared/components/PermissionWrapper/PermissionWrapper.tsx';
import {
	PermissionRole,
	ResourceType,
} from '@dealstack/shared/lib/common/fineGrainedAuthorization/permissions.ts';
import { defaultToEmptyString } from '@dealstack/shared/lib/format/string.ts';
import { CapExportMenu } from '@dealstack/frontend/app/pages/Project/Cap/CapChartPage/CapChartPageHeader/CapExportMenu.tsx';
import { HoldingsTableSettings } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/HoldingsTableSettings.tsx';
import { StorageKeys } from '@dealstack/frontend/shared/helper/storageUtils.ts';
import { useDealstackFeatureFlag } from '@dealstack/frontend/shared/hooks/useDealstackFeatureFlag/useDealstackFeatureFlag.hook.ts';
import { FEATURE_FLAG } from '@dealstack/shared/lib/common/featureFlag.ts';
import { useDownloadVestingEventsReport } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetails/components/useDownloadVestingEventsReport';
import { FullscreenLoader } from '@dealstack/frontend/shared/components/loaders/FullLoaderScreenLoader/FullLoaderScreenLoader.tsx';
import { ShareRegisterPdfDrawer } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/Holdings/ShareRegisterPdfDrawer/ShareRegisterPdfDrawer';
import { TitleRailToggle } from '@dealstack/frontend/app/pages/Project/Cap/common/TitleRailToggle/TitleRailToggle.tsx';

interface Props {
	selectedEntityLoading?: boolean;
}

export const Ownership = ({ selectedEntityLoading }: Props) => {
	const tCapManage = useTranslationCapManage();
	const { dealId } = useContext(DealContext) || {};
	const {
		showSummarized,
		showPercentages,
		selectedEntityId,
		filterDate,
		setFilterDate,
		getOwnershipListRequest,
		viewMode,
		setViewMode,
		isEmptyState,
		onSummarizedChange,
		onShowPercentagesChange,
		refetchHoldingsList,
		selectedEntityData,
		viewByBlock,
		rail,
		setRail,
		railEnabled,
		effectiveRail,
	} = useHoldingsController({ tableType: HoldingsType.OwnershipTable });

	// CAP-1976: the nominee gate (feature flag AND deal master switch AND this issuer's own flag)
	// lives in useHoldingsController, so the table request, the toggle and the exports share one
	// predicate.
	const viewOptions = useMemo(
		() => [
			{
				label: tCapManage('holdings.wideView'),
				value: ViewMode.Wide,
			},
			{
				label: tCapManage('holdings.regularView'),
				value: ViewMode.Regular,
			},
			{
				label: tCapManage('holdings.blockView'),
				value: ViewMode.Block,
			},
		],
		[tCapManage]
	);
	const { value: vestingReportFeatureFlag } = useDealstackFeatureFlag(
		FEATURE_FLAG.VESTING_REPORT_EXPORT
	);

	const {
		downloadCSV,
		downloadExcel,
		downloadShareRegister,
		shareRegisterDrawer,
		isLoading: isBulkActionLoading,
	} = useHoldingsBulkAction(viewMode, HoldingsType.OwnershipTable, filterDate, effectiveRail);

	const { onDownload: downloadVestingReport, isDownloading: isVestingReportDownloading } =
		useDownloadVestingEventsReport({
			dealId,
			issuerId: selectedEntityId,
		});

	const breadcrumbs = useMemo(
		() =>
			DIRECTORY_BREADCRUMBS[PEOPLE_ROUTES.OWNERSHIP]({
				tCapManage,
				dealId,
				personId: selectedEntityId,
				personDisplayName: selectedEntityData?.displayName,
				noLink: true,
			}),
		[tCapManage, dealId, selectedEntityId, selectedEntityData]
	);

	useBreadcrumbs(breadcrumbs);

	const isPageLoading = !!(getOwnershipListRequest.isLoading || selectedEntityLoading);
	const pdfDownloadOption = {
		label: tCapManage('holdings.download_share_register'),
		onClick: () => downloadShareRegister(),
		isLoading: isBulkActionLoading,
		dataTestId: TEST_ELEMENT_IDS.CAP_OWNERSHIP_DOWNLOAD_SHARE_REGISTER_BUTTON,
	};

	return (
		<Flex direction="column" p="1rem" h="100%" gap="1rem">
			{isBulkActionLoading && <FullscreenLoader name="pageLoading" />}
			<Flex justify="space-between">
				<DateSelectionMenu
					dateTo={filterDate}
					maxDate={new Date()}
					setFilterDate={setFilterDate}
				/>
				<Flex gap="0.5rem" align="center">
					{railEnabled && (
						<TitleRailToggle key="titleRail" rail={rail} setRail={setRail} />
					)}
					<LabeledSegmentedControl<ViewMode>
						key="viewMode"
						data-testid={TEST_ELEMENT_IDS.CAP_OWNERSHIP_VIEW_SWITCH}
						label={tCapManage('holdings.viewLabel')}
						value={viewMode}
						onChange={setViewMode}
						data={viewOptions}
					/>

					<PermissionWrapper
						key="exportMenu"
						resourceType={ResourceType.DEAL}
						resourceId={defaultToEmptyString(dealId)}
						permissionRole={PermissionRole.EXPORT_VIEWER}
					>
						<CapExportMenu
							options={[
								{
									label: tCapManage('holdings.download_xlsx'),
									onClick: () => downloadExcel(),
									dataTestId: TEST_ELEMENT_IDS.CAP_OWNERSHIP_DOWNLOAD_XLSX_BUTTON,
								},
								{
									label: tCapManage('holdings.download_csv'),
									onClick: () => downloadCSV(),
									dataTestId: TEST_ELEMENT_IDS.CAP_OWNERSHIP_DOWNLOAD_CSV_BUTTON,
								},
								pdfDownloadOption,
								...(vestingReportFeatureFlag
									? [
											{
												label: tCapManage(
													'holdings.download_vesting_report'
												),
												onClick: () => downloadVestingReport(),
												isLoading: isVestingReportDownloading,
											},
										]
									: []),
							]}
						/>
					</PermissionWrapper>
					<HoldingsTableSettings
						key="settingsUI"
						showSummarized={showSummarized}
						onSummarizedChange={onSummarizedChange}
						showPercentages={showPercentages}
						onShowPercentagesChange={onShowPercentagesChange}
						tableType={HoldingsType.OwnershipTable}
					/>
				</Flex>
			</Flex>

			{isEmptyState && !isPageLoading ? (
				<EmptyState title={tCapManage('holdings.empty_state')} icon="es-pie-chart" />
			) : (
				// Render the table (not nothing) while loading so its own loader
				// covers the fetch — gating on !isPageLoading left a blank page.
				// Mirrors the Holdings tab.
				<>
					{viewMode === ViewMode.Wide && (
						<WideViewOwnershipTable
							showSummarized={showSummarized}
							showPercentages={showPercentages}
							isOwnership={getOwnershipListRequest.data ?? []}
							isLoading={isPageLoading}
							downloadCSV={downloadCSV}
						/>
					)}
					{viewMode !== ViewMode.Wide && (
						<RegularViewTable
							showSummarized={showSummarized}
							showPercentages={showPercentages}
							tableData={getOwnershipListRequest.data ?? []}
							isLoading={isPageLoading}
							tableType={HoldingsType.OwnershipTable}
							refetchHoldingsList={refetchHoldingsList}
							viewByBlock={viewByBlock}
							filterDate={filterDate}
							colPreferencesKey={StorageKeys.CAP_OWNERSHIP_REGULAR_TABLE_COLS}
							rail={effectiveRail}
						/>
					)}
				</>
			)}
			<ShareRegisterPdfDrawer {...shareRegisterDrawer} />
		</Flex>
	);
};
